import { Box, Button, Divider, MenuItem, TextField, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import type { CareTeamSwapLookups } from './CareTeamSwapService'
import type { RoleGroup } from './roles'
import { NumberedSectionCard } from '~/shared/ui'

export interface CohortFilters {
  state: string
  contract: string
  enrollmentStatus: string
}

interface CurrentTeamSectionProps {
  lookups: CareTeamSwapLookups
  roles: RoleGroup[]
  currentByRole: Record<string, string>
  onCurrentChange: (roleName: string, value: string) => void
  filters: CohortFilters
  onFilterChange: (patch: Partial<CohortFilters>) => void
  onSearch: () => void
  onReset: () => void
  canSearch: boolean
  isSearching: boolean
  collapsed: boolean
  onToggleCollapsed: () => void
  // Result count shown as a badge on the collapsed summary; null before a search has run.
  resultCount: number | null
}

// Four columns so the six care-team roles wrap into two rows (Physician / NP / RN / HG, then BH / MG).
const ROLE_GRID = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
}

const FILTER_GRID = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
}

/**
 * Picks the departing (current) provider per role plus the optional state / contract / enrollment
 * cohort filters. Search needs at least one current provider; the endpoint enforces the same rule.
 */
const CurrentTeamSection = ({
  lookups,
  roles,
  currentByRole,
  onCurrentChange,
  filters,
  onFilterChange,
  onSearch,
  onReset,
  canSearch,
  isSearching,
  collapsed,
  onToggleCollapsed,
  resultCount,
}: CurrentTeamSectionProps) => {
  const departing = roles
    .filter(role => currentByRole[role.name])
    .map(role => {
      const provider = role.providers.find(p => String(p.id) === currentByRole[role.name])
      return `${role.abbr} ${provider?.full_name ?? ''}`.trim()
    })
  const filterLabels = [
    filters.state && lookups.states.find(s => String(s.id) === filters.state)?.abbreviation,
    filters.contract && lookups.contracts.find(c => String(c.id) === filters.contract)?.name,
    filters.enrollmentStatus || undefined,
  ].filter(Boolean)

  const summary = (
    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
      {departing.length ? departing.join(', ') : 'No departing providers selected'}
      {filterLabels.length > 0 ? ` · ${filterLabels.join(' · ')}` : ''}
      {resultCount != null && (
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {' '}
          · {resultCount.toLocaleString()} members
        </Box>
      )}
    </Typography>
  )

  return (
    <NumberedSectionCard
      index={1}
      title="Current care team"
      hint="Pick one or more departing providers, then optionally narrow by state, contract, or primary-care enrollment. At least one provider is required before you can search."
      collapsible
      collapsed={collapsed}
      onToggleCollapsed={onToggleCollapsed}
      summary={summary}
      testId="current-team-card"
    >
      <Box sx={ROLE_GRID}>
        {roles.map(role => (
          <TextField
            key={role.name}
            select
            label={`${role.name} (${role.abbr})`}
            value={currentByRole[role.name] ?? ''}
            onChange={event => onCurrentChange(role.name, event.target.value)}
            data-testid={`current-${role.name}`}
          >
            <MenuItem value="">— Any</MenuItem>
            {role.providers.map(provider => (
              <MenuItem key={provider.id} value={String(provider.id)}>
                {provider.full_name}
              </MenuItem>
            ))}
          </TextField>
        ))}
      </Box>

      <Divider sx={{ my: 2.5 }} />
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: 'text.secondary',
          mb: 1.5,
        }}
      >
        Narrow by (optional)
      </Typography>
      <Box sx={FILTER_GRID}>
        <TextField
          select
          label="State of residence"
          value={filters.state}
          onChange={event => onFilterChange({ state: event.target.value })}
          data-testid="state-filter"
        >
          <MenuItem value="">All states</MenuItem>
          {lookups.states.map(state => (
            <MenuItem key={state.id} value={String(state.id)}>
              {state.abbreviation} — {state.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Contract"
          value={filters.contract}
          onChange={event => onFilterChange({ contract: event.target.value })}
          data-testid="contract-filter"
        >
          <MenuItem value="">All contracts</MenuItem>
          {lookups.contracts.map(contract => (
            <MenuItem key={contract.id} value={String(contract.id)}>
              {contract.name ?? `Contract ${contract.id}`}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Primary care enrollment"
          value={filters.enrollmentStatus}
          onChange={event => onFilterChange({ enrollmentStatus: event.target.value })}
          data-testid="enrollment-filter"
        >
          <MenuItem value="">Any status</MenuItem>
          {lookups.primary_care_enrollment_statuses.map(status => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={onSearch}
          disabled={!canSearch || isSearching}
          data-testid="search-button"
        >
          Search members
        </Button>
        <Button color="inherit" onClick={onReset} data-testid="reset-button">
          Reset
        </Button>
        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
          {canSearch
            ? 'Add state, contract or enrollment filters to narrow the panel, or search now.'
            : 'Select at least one current care team member to enable search.'}
        </Typography>
      </Box>
    </NumberedSectionCard>
  )
}

export default CurrentTeamSection
